import { useEffect, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAdmin, type OrgRow, type Tier } from '@/hooks/useAdmin'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PaginationControls } from '@/components/ui/pagination-controls'
import type React from 'react'
import { Building2, Search, ChevronRight } from 'lucide-react'

const PAGE_SIZE = 25

const TIERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All tiers' },
  { value: 'free', label: 'Free' },
  { value: 'team', label: 'Team' },
  { value: 'scale', label: 'Scale' },
  { value: 'enterprise', label: 'Enterprise' },
]

function tierBadge(tier: Tier) {
  const map: Record<Tier, string> = {
    free: 'text-muted-foreground border-border',
    team: 'text-primary border-primary/40 bg-primary/10',
    scale: 'border-border bg-muted/50',
    enterprise: 'text-success border-success/40 bg-success/10',
  }
  return map[tier] ?? 'text-muted-foreground border-border'
}

function tierInlineStyle(tier: Tier): React.CSSProperties {
  if (tier === 'scale') return { color: 'hsl(var(--amber))' }
  return {}
}

function subStatusPill(status: string) {
  if (status === 'active') return 'status-pill status-pill-success'
  if (status === 'trialing') return 'status-pill status-pill-muted'
  if (status === 'past_due' || status === 'unpaid') return 'status-pill status-pill-danger'
  return 'status-pill status-pill-muted'
}

export function OrganizationsPage() {
  const { listOrganizations } = useAdmin()

  const [orgs, setOrgs] = useState<OrgRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [tierFilter, setTierFilter] = useState('all')
  const [page, setPage] = useState(1)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetch = useCallback(
    (s: string, tier: string, p: number) => {
      setLoading(true)
      setError(null)
      listOrganizations({
        search: s || undefined,
        tier: tier !== 'all' ? tier : undefined,
        limit: PAGE_SIZE,
        offset: (p - 1) * PAGE_SIZE,
      })
        .then(({ organizations, total: t }) => {
          setOrgs(organizations)
          setTotal(t)
        })
        .catch(() => setError('Failed to load organizations'))
        .finally(() => setLoading(false))
    },
    [listOrganizations],
  )

  // Initial + page/filter changes
  useEffect(() => {
    fetch(search, tierFilter, page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierFilter, page])

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearch(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPage(1)
      fetch(value, tierFilter, 1)
    }, 300)
  }

  const handleTierChange = (value: string) => {
    setTierFilter(value)
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="brand-wordmark text-2xl">Organizations</h1>
        <p className="kicker text-muted-foreground mt-1">
          {total.toLocaleString()} organization{total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search organizations…"
            className="pl-8 h-9 text-sm"
          />
        </div>
        <Select value={tierFilter} onValueChange={handleTierChange}>
          <SelectTrigger className="h-9 w-[140px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIERS.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        {error ? (
          <CardContent className="p-6 text-sm text-destructive">{error}</CardContent>
        ) : loading ? (
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-3.5">
                  <div className="h-4 w-48 rounded bg-muted/40 animate-pulse" />
                  <div className="h-4 w-16 rounded bg-muted/30 animate-pulse" />
                  <div className="ml-auto h-4 w-24 rounded bg-muted/30 animate-pulse" />
                </div>
              ))}
            </div>
          </CardContent>
        ) : orgs.length === 0 ? (
          <CardContent className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Building2 className="h-8 w-8 opacity-40" />
            <p className="text-sm">No organizations found</p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            {/* Header */}
            <div className="grid grid-cols-[1fr_100px_70px_70px_120px_110px_28px] gap-4 px-6 py-2.5 border-b text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Name</span>
              <span>Tier</span>
              <span className="text-right">Members</span>
              <span className="text-right">Conns</span>
              <span>Sub status</span>
              <span>Created</span>
              <span />
            </div>
            <div className="divide-y divide-border">
              {orgs.map((org) => (
                <Link
                  key={org.id}
                  to={`/orgs/${org.id}`}
                  className="grid grid-cols-[1fr_100px_70px_70px_120px_110px_28px] gap-4 px-6 py-3.5 items-center hover:bg-accent/40 transition-colors group"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{org.name}</p>
                    <p className="mono-accent text-muted-foreground truncate">{org.id}</p>
                  </div>
                  <div>
                    <Badge
                      variant="outline"
                      className={`text-xs capitalize ${tierBadge(org.tier)}`}
                      style={tierInlineStyle(org.tier)}
                    >
                      {org.tier}
                    </Badge>
                  </div>
                  <p className="text-sm text-right tabular-nums">{org.member_count}</p>
                  <p className="text-sm text-right tabular-nums">{org.connection_count}</p>
                  <div>
                    <span className={subStatusPill(org.subscription_status)}>
                      {org.subscription_status || 'none'}
                    </span>
                  </div>
                  <p className="mono-accent text-muted-foreground">
                    {new Date(org.created_at).toLocaleDateString()}
                  </p>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
                </Link>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pagination */}
      {!loading && !error && total > 0 && (
        <PaginationControls
          page={page}
          totalPages={totalPages}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
